import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, Unlock, Eye, EyeOff, AlertCircle, CheckCircle, Shield, DollarSign } from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";

export const SettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Auth settings
  const [authStatus, setAuthStatus] = useState({ auth_required: false });
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [authSaving, setAuthSaving] = useState(false);
  const [authMessage, setAuthMessage] = useState({ type: "", text: "" });

  useEffect(() => { 
    axios.get(`${API}/settings/site`).then(r => setSettings(r.data));
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/status`);
      setAuthStatus(response.data);
    } catch (err) {
      console.error("Error checking auth status:", err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await axios.put(`${API}/settings/site`, settings, { headers: getAuthHeader() });
    setSaving(false);
  };

  const handleSetCredentials = async (e) => {
    e.preventDefault();
    setAuthMessage({ type: "", text: "" });

    if (newPassword !== confirmPassword) {
      setAuthMessage({ type: "error", text: "Passwords do not match" });
      return;
    }

    if (newPassword.length < 8) {
      setAuthMessage({ type: "error", text: "Password must be at least 8 characters" });
      return;
    }

    setAuthSaving(true);
    try {
      const response = await axios.post(`${API}/auth/set-credentials`, {
        email: newEmail,
        password: newPassword
      }, { headers: getAuthHeader() });

      // Update stored token
      localStorage.setItem("auth_token", response.data.token);
      localStorage.setItem("auth_email", response.data.email);
      localStorage.setItem("auth_expires", response.data.expires_at);

      setAuthMessage({ type: "success", text: "Admin credentials set successfully! Login is now required." });
      setNewEmail("");
      setNewPassword("");
      setConfirmPassword("");
      checkAuthStatus();
    } catch (err) {
      setAuthMessage({ type: "error", text: err.response?.data?.detail || "Failed to set credentials" });
    }
    setAuthSaving(false);
  };

  const handleRemoveCredentials = async () => {
    if (!window.confirm("Are you sure you want to remove admin credentials? This will make the admin panel open to everyone.")) {
      return;
    }

    setAuthSaving(true);
    try {
      await axios.post(`${API}/auth/remove-credentials`, {
        password: currentPassword
      }, { headers: getAuthHeader() });

      setAuthMessage({ type: "success", text: "Credentials removed. Admin is now open." });
      setCurrentPassword("");
      checkAuthStatus();
    } catch (err) {
      setAuthMessage({ type: "error", text: err.response?.data?.detail || "Failed to remove credentials" });
    }
    setAuthSaving(false);
  };

  if (!settings) return <div className="text-center py-8"><div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mx-auto"></div></div>;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      {/* Admin Security Section */}
      <div className="p-6 bg-zinc-900/50 border border-white/10">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-6 h-6 text-white" />
          <div>
            <h2 className="font-secondary text-xl font-bold uppercase tracking-tight text-white">Admin Security</h2>
            <p className="text-zinc-500 text-sm">
              {authStatus.auth_required 
                ? "Admin is protected with login credentials" 
                : "Admin is currently open to everyone"}
            </p>
          </div>
          <div className={`ml-auto px-3 py-1 text-xs font-bold uppercase ${authStatus.auth_required ? 'bg-green-600 text-white' : 'bg-yellow-600 text-black'}`}>
            {authStatus.auth_required ? <><Lock className="w-3 h-3 inline mr-1" /> Locked</> : <><Unlock className="w-3 h-3 inline mr-1" /> Open</>}
          </div>
        </div>

        {/* Message */}
        {authMessage.text && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-center gap-3 p-4 mb-6 ${authMessage.type === 'error' ? 'bg-red-500/10 border border-red-500/20' : 'bg-green-500/10 border border-green-500/20'}`}
          >
            {authMessage.type === 'error' ? (
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            ) : (
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            )}
            <span className={authMessage.type === 'error' ? 'text-red-500' : 'text-green-500'}>{authMessage.text}</span>
          </motion.div>
        )}

        {!authStatus.auth_required ? (
          /* Set Credentials Form */
          <form onSubmit={handleSetCredentials} className="space-y-4">
            <p className="text-zinc-400 text-sm mb-4">
              Set admin credentials to protect the admin panel. Anyone trying to access /admin will need to login.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Admin Email</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-white/30"
                />
              </div>
              <div></div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min 8 characters"
                    required
                    className="w-full bg-black border border-white/10 text-white px-3 py-2 pr-10 text-sm focus:outline-none focus:border-white/30"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Confirm Password</label>
                <input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  required
                  className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-white/30"
                />
              </div>
            </div>
            <Button type="submit" disabled={authSaving} className="bg-green-600 text-white hover:bg-green-700 text-xs px-4 py-2 h-auto rounded-none">
              <Lock className="w-4 h-4 mr-2" />
              {authSaving ? "Setting..." : "Set Credentials & Lock Admin"}
            </Button>
          </form>
        ) : (
          /* Remove Credentials Form */
          <div className="space-y-4">
            <p className="text-zinc-400 text-sm">
              To remove credentials and make admin open again, enter your current password below.
            </p>
            <div className="flex items-end gap-4">
              <div className="flex-1 max-w-xs">
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-white/30"
                />
              </div>
              <Button 
                onClick={handleRemoveCredentials} 
                disabled={authSaving || !currentPassword} 
                className="bg-red-600 text-white hover:bg-red-700 text-xs px-4 py-2 h-auto rounded-none"
              >
                <Unlock className="w-4 h-4 mr-2" />
                {authSaving ? "Removing..." : "Remove Credentials"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Site Settings */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">Site Settings</h2>
          <Button onClick={handleSave} disabled={saving} className="bg-white text-black text-xs px-4 py-2 h-auto rounded-none">{saving ? "Saving..." : "Save"}</Button>
        </div>
        <div className="p-6 bg-zinc-900/50 border border-white/5 space-y-4">
          <div>
            <label className="block text-xs text-zinc-500 uppercase mb-2">Site Name</label>
            <input type="text" value={settings.site_name} onChange={(e) => setSettings({ ...settings, site_name: e.target.value })} className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 uppercase mb-2">Tagline</label>
            <input type="text" value={settings.tagline} onChange={(e) => setSettings({ ...settings, tagline: e.target.value })} className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 uppercase mb-2">Logo URL</label>
            <input type="text" value={settings.logo_url} onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })} className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" />
          </div>
        </div>
      </div>

      {/* Google AdSense Settings */}
      <div className="p-6 bg-zinc-900/50 border border-white/10">
        <div className="flex items-center gap-3 mb-6">
          <DollarSign className="w-6 h-6 text-green-500" />
          <div>
            <h2 className="font-secondary text-xl font-bold uppercase tracking-tight text-white">Google AdSense</h2>
            <p className="text-zinc-500 text-sm">Configure ad placements across the site</p>
          </div>
          <div className="ml-auto px-3 py-1 text-xs font-bold uppercase bg-green-600 text-white">
            Ready
          </div>
        </div>

        <div className="bg-green-500/10 border border-green-500/20 p-4 mb-6">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-green-500 font-medium">AdSense Meta Tag Added</p>
              <p className="text-zinc-400 text-sm mt-1">
                Publisher ID: <code className="bg-black px-2 py-0.5 text-green-400">ca-pub-9517445443217028</code>
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-zinc-400 text-sm">
            Ad slots are configured in the code. After Google approves your site, ads will automatically appear in these locations:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-black/50 border border-white/5 p-4">
              <h4 className="text-white font-bold text-sm mb-2">News Articles</h4>
              <p className="text-zinc-500 text-xs">In-article ads appear within article content</p>
            </div>
            <div className="bg-black/50 border border-white/5 p-4">
              <h4 className="text-white font-bold text-sm mb-2">Schedule Page</h4>
              <p className="text-zinc-500 text-xs">Leaderboard ads between schedule date groups</p>
            </div>
            <div className="bg-black/50 border border-white/5 p-4">
              <h4 className="text-white font-bold text-sm mb-2">On Demand</h4>
              <p className="text-zinc-500 text-xs">In-feed ads between video listings</p>
            </div>
            <div className="bg-black/50 border border-white/5 p-4">
              <h4 className="text-white font-bold text-sm mb-2">Homepage</h4>
              <p className="text-zinc-500 text-xs">Auto ads placement by Google</p>
            </div>
          </div>

          <div className="pt-4 border-t border-white/10">
            <p className="text-zinc-500 text-xs">
              <strong className="text-zinc-400">Note:</strong> To add custom ad slot IDs, update the <code className="bg-black px-1">slot</code> prop 
              in <code className="bg-black px-1">AdUnit</code> components. Get slot IDs from your 
              <a href="https://www.google.com/adsense" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline ml-1">
                Google AdSense dashboard
              </a>.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SettingsTab;
